import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { prefersReducedMotion } from '#app/utils/misc.tsx'
import { Dot } from './__dot.tsx'

/** Final value of the demo build-progress bar, in percent. */
const BUILD_PROGRESS = 72

/**
 * Landing hero. Renders eyebrow, headline, lead, two CTAs, and a dark product
 * panel with a build-progress bar and a floating "All checks passed" chip.
 *
 * Motion is progressive enhancement. The resting/final state renders
 * server-side — every child is visible and the progress bar is full — so no-JS
 * and reduced-motion visitors see the complete hero. When motion is allowed,
 * mounting sets `data-in` (replaying the children's rise/fade via CSS) and the
 * bar animates 0 → full. All entrance and ambient loops are gated behind
 * `prefers-reduced-motion: no-preference` in `app/styles/tailwind.css`.
 */
export function Hero() {
	const { t } = useTranslation('public')
	const ref = useRef<HTMLElement>(null)
	const [fill, setFill] = useState(BUILD_PROGRESS)

	useEffect(() => {
		if (prefersReducedMotion()) return
		const el = ref.current
		setFill(0)
		const enter = window.setTimeout(() => el?.setAttribute('data-in', ''), 60)
		const grow = window.setTimeout(() => setFill(BUILD_PROGRESS), 480)
		return () => {
			window.clearTimeout(enter)
			window.clearTimeout(grow)
		}
	}, [])

	return (
		<section
			id="hero"
			ref={ref}
			aria-labelledby="hero-heading"
			className="hero relative scroll-mt-20 overflow-hidden"
		>
			{/* Technical-lattice backdrop (behind the z-1 content). A fine grid that
			    fades out toward the edges via a radial mask, warmed by one soft,
			    breathing brand glow in the top-right where the product panel sits.
			    Decorative + motion-safe (the glow's breathe is gated in tailwind.css;
			    the grid is static). The grid line derives from `--border`, so it
			    re-tints with light/dark on its own. */}
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0"
				style={{
					backgroundImage: `
						linear-gradient(to right, color-mix(in oklch, var(--border) 70%, transparent) 1px, transparent 1px),
						linear-gradient(to bottom, color-mix(in oklch, var(--border) 70%, transparent) 1px, transparent 1px)`,
					backgroundSize: '46px 46px',
					maskImage:
						'radial-gradient(115% 115% at 72% 12%, #000 22%, transparent 72%)',
					WebkitMaskImage:
						'radial-gradient(115% 115% at 72% 12%, #000 22%, transparent 72%)',
				}}
			/>
			<div
				aria-hidden
				className="bg-brand-glow breathe pointer-events-none absolute -top-40 -right-24 h-[560px] w-[560px] rounded-full opacity-70 blur-3xl"
			/>

			<div className="relative z-1 container grid items-center gap-[clamp(24px,4vw,56px)] py-[clamp(36px,6vw,76px)] min-[880px]:grid-cols-[0.95fr_1.05fr]">
				<div>
					<p className="anim d1 text-brand inline-flex items-center gap-2.5 text-body-xs font-semibold tracking-eyebrow uppercase">
						<span className="bg-brand dotpulse h-[7px] w-[7px] rounded-full" />
						{t('landing.hero.eyebrow')}
					</p>
					<h1
						id="hero-heading"
						className="anim d2 mt-5 text-[clamp(40px,4.6vw,62px)] leading-[1.02] font-semibold tracking-mega text-balance"
					>
						<Trans
							t={t}
							i18nKey="landing.hero.heading"
							components={{ 1: <span className="text-brand" /> }}
						/>
					</h1>
					<p className="anim d3 text-muted-foreground mt-5 max-w-[34ch] text-lg leading-[1.55] text-pretty">
						{t('landing.hero.lead')}
					</p>
					<div className="anim d4 mt-8 flex flex-wrap items-center gap-3.5">
						<Button asChild size="lg">
							<a href="#contact">{t('landing.hero.ctaPrimary')}</a>
						</Button>
						<Button asChild size="lg" variant="outline">
							<a href="#work">{t('landing.hero.ctaSecondary')}</a>
						</Button>
					</div>
					<div className="anim d5 text-muted-foreground mt-6 flex flex-wrap items-center gap-2.5 text-body-xs">
						<span>{t('landing.hero.metaBooking')}</span>
						<Dot />
						<span>{t('landing.hero.metaSprints')}</span>
						<Dot />
						<span>{t('landing.hero.metaDesignBuild')}</span>
					</div>
				</div>

				{/* Right product panel — `.theme-inverse` renders it as the opposite of
				    the active page theme (dark panel on a light page, light panel on a
				    dark page) so it always keeps contrast. `contents` keeps the absolute
				    glow + chip positioned against the outer cell while the panel inherits
				    the inverted tokens. */}
				<div className="anim d4 relative hidden items-center justify-center min-[880px]:flex">
					<div className="theme-inverse contents">
						<div
							aria-hidden
							className="bg-brand-glow breathe absolute h-[78%] w-[78%] rounded-[40px] opacity-90 blur-[10px]"
						/>
						<div className="bg-card text-card-foreground border-border relative w-[min(420px,92%)] rounded-[20px] border p-[26px] shadow-[0_30px_70px_-30px_rgba(0,0,0,0.55)]">
							<div className="flex items-center gap-3">
								<div className="bg-brand h-[42px] w-[42px] rounded-xl" />
								<div>
									<div className="text-[15.5px] font-semibold">atlas.app</div>
									<div className="text-muted-foreground text-[13px]">
										{t('landing.hero.panel.deployment')}
									</div>
								</div>
								<span className="bg-brand-soft text-brand ml-auto inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-body-2xs font-medium">
									<span className="bg-brand h-1.5 w-1.5 rounded-full" />
									{t('landing.hero.panel.live')}
								</span>
							</div>
							<div className="text-muted-foreground mt-6 mb-2 flex justify-between text-[13px]">
								<span>{t('landing.hero.panel.building')}</span>
								<b className="text-card-foreground">{fill}%</b>
							</div>
							<div className="bg-muted h-2 overflow-hidden rounded-full">
								<div
									className="bg-brand h-full rounded-full transition-[width] duration-[1400ms] ease-[cubic-bezier(0.22,0.7,0.25,1)]"
									style={{ width: `${fill}%` }}
								/>
							</div>
							<div className="mt-6 flex gap-2.5">
								<Button className="flex-1">
									{t('landing.hero.panel.deploy')}
								</Button>
								<Button variant="outline">
									{t('landing.hero.panel.preview')}
								</Button>
							</div>
						</div>
						<div className="bg-card text-card-foreground border-border floaty absolute -top-4 -left-3.5 flex items-center gap-2 rounded-xl border px-[15px] py-2.5 text-[13px] font-medium shadow-[0_18px_40px_-18px_rgba(0,0,0,0.6)]">
							<span className="bg-brand grid h-[18px] w-[18px] place-items-center rounded-full text-white">
								<Icon name="check" className="h-3 w-3" />
							</span>
							{t('landing.hero.panel.checksPassed')}
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}
